import { h, createContext } from 'preact';
import { useState, useEffect, useContext } from 'preact/hooks';
import { useAway, AwayBadge, awayText } from '../utils/away.js';

// Who is away now (vakantiedelegatie), for the labels on members
const AwayContext = createContext({});
import { api } from '../state/auth.js';
import Modal from '../components/shared/Modal.jsx';

function Avatar({ name, avatarUrl, size = 36 }) {
  if (avatarUrl) {
    return (
      <div style={`width:${size}px;height:${size}px;border-radius:50%;overflow:hidden;flex-shrink:0`}>
        <img src={avatarUrl} alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
      </div>
    );
  }
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const colors = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
  const color = colors[(name || '').charCodeAt(0) % colors.length];
  return (
    <div style={`width:${size}px;height:${size}px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:${size * 0.38}px;font-weight:700;color:#fff;flex-shrink:0`}>
      {initials}
    </div>
  );
}

function MemberRow({ member, onSelect }) {
  const away = useContext(AwayContext)[member.id];
  return (
    <div
      onClick={() => onSelect && onSelect(member)}
      style={`display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--color-border);${onSelect ? 'cursor:pointer' : ''}`}
    >
      <Avatar name={member.display_name} avatarUrl={member.avatar_url} size={32} />
      <div style="min-width:0;flex:1">
        <div style="font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
          {member.display_name}
        </div>
        {away && <div style="margin:2px 0"><AwayBadge away={away} /></div>}
        {member.roles.length > 0 && (
          <div style="font-size:11px;color:var(--color-text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            {member.roles.join(' · ')}
          </div>
        )}
      </div>
      {onSelect && <span style="font-size:12px;color:var(--color-text-muted);flex-shrink:0">›</span>}
    </div>
  );
}

function MemberProfileModal({ member, onClose }) {
  const awayAll = useContext(AwayContext);
  if (!member) return null;
  const away = awayAll[member.id];
  const firstName = member.display_name?.split(' ')[0] || member.display_name;

  return (
    <div
      style="position:fixed;inset:0;background:rgba(0,0,0,0.45);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:50;padding:16px"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style="background:var(--color-surface);border-radius:20px;width:100%;max-width:580px;max-height:90vh;overflow-y:auto;box-shadow:0 24px 64px rgba(0,0,0,0.25);position:relative">

        {/* Coloured banner — avatar is absolute at bottom so banner never bleeds through */}
        <div style="background:var(--color-primary);border-radius:20px 20px 0 0;height:116px;position:relative;flex-shrink:0">
          <button
            onClick={onClose}
            style="position:absolute;top:12px;right:12px;background:rgba(255,255,255,0.2);border:none;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#fff;font-size:16px;line-height:1"
          >×</button>
          {/* Avatar sits at the banner's bottom edge, half in / half out */}
          <div style="position:absolute;bottom:-48px;left:50%;transform:translateX(-50%);border-radius:50%;border:4px solid var(--color-surface);overflow:hidden;box-shadow:0 4px 16px rgba(0,0,0,0.15)">
            <Avatar name={member.display_name} avatarUrl={member.avatar_url} size={92} />
          </div>
        </div>

        {/* Content — padding-top makes room for the overlapping avatar */}
        <div style="display:flex;flex-direction:column;align-items:center;padding:60px 28px 28px">

          <h2 style="margin:14px 0 6px;font-size:21px;font-weight:800;text-align:center;color:var(--color-text)">{member.display_name}</h2>
          {away && <div style="margin-bottom:8px"><AwayBadge away={away} /></div>}

          {/* Role badges */}
          <div style="display:flex;flex-wrap:wrap;gap:6px;justify-content:center;margin-bottom:8px">
            {member.roles.map(r => (
              <span key={r} style="font-size:12px;background:var(--color-primary);color:#fff;padding:3px 12px;border-radius:20px;font-weight:600">{r}</span>
            ))}
          </div>

          {/* Email */}
          {member.email && (
            <a href={`mailto:${member.email}`} style="font-size:12px;color:var(--color-text-muted);text-decoration:none;margin-bottom:20px">
              {member.email}
            </a>
          )}

          {/* Divider */}
          <div style="width:100%;height:1px;background:var(--color-border);margin:4px 0 20px" />

          {/* Bio sections */}
          <div style="width:100%;display:flex;flex-direction:column;gap:18px">
            {(member.bio || true) && (
              <div>
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                  <span style="font-size:15px">👤</span>
                  <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted)">Over {firstName}</span>
                </div>
                <p style="font-size:13px;line-height:1.65;margin:0;color:var(--color-text)">
                  {member.bio || `${firstName} is een betrokken lid van de club en draagt actief bij. Meer informatie volgt binnenkort.`}
                </p>
              </div>
            )}

            {(member.contact_for || true) && (
              <div>
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                  <span style="font-size:15px">💬</span>
                  <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted)">Waarvoor bij {firstName}?</span>
                </div>
                <p style="font-size:13px;line-height:1.65;margin:0;color:var(--color-text)">
                  {member.contact_for || `Vragen over de rol van ${firstName}? Neem gerust contact op via het e-mailadres hierboven.`}
                </p>
              </div>
            )}

            {member.fun_fact && (
              <div style="background:linear-gradient(135deg,#eff6ff,#f0fdf4);border-radius:12px;padding:14px 16px;border:1px solid var(--color-border)">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                  <span style="font-size:15px">⚡</span>
                  <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-primary)">Leuk feitje</span>
                </div>
                <p style="font-size:13px;line-height:1.65;margin:0;color:var(--color-text)">{member.fun_fact}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact marker in a commission card: away, with the substitute on hover
function AwayMark({ userId }) {
  const away = useContext(AwayContext)[userId];
  return away ? <span title={`${awayText(away)} · vervanger ${away.delegate_name}`} aria-label={`${awayText(away)}, vervanger ${away.delegate_name}`} style="flex-shrink:0">🌴</span> : null;
}

function CommissionCard({ commission, onSelect }) {
  const [open, setOpen] = useState(true);
  const supervisors = commission.supervisors || [];
  const totalCount = commission.members.length + supervisors.length;

  // Group members by role for the sub-level view
  const roleGroups = [];
  const seen = new Map();
  for (const m of commission.members) {
    for (const role of (m.roles.length ? m.roles : [''])) {
      if (!seen.has(role)) { seen.set(role, []); roleGroups.push(role); }
      seen.get(role).push(m);
    }
  }
  const grouped = roleGroups.length > 1 || (roleGroups.length === 1 && roleGroups[0] !== '');

  return (
    <div style="border:1px solid var(--color-border);border-radius:10px;overflow:hidden;background:var(--color-surface)">
      <button
        onClick={() => setOpen(o => !o)}
        style="width:100%;display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:none;border:none;cursor:pointer;text-align:left;gap:8px"
      >
        <div style="display:flex;align-items:center;gap:8px;min-width:0">
          <span style="font-size:13px;font-weight:600;color:var(--color-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            {commission.name}
          </span>
          <span style="flex-shrink:0;font-size:11px;font-weight:600;padding:1px 7px;border-radius:10px;background:var(--color-primary-light,#ede9fe);color:var(--color-primary)">
            {totalCount}
          </span>
        </div>
        <span style={`font-size:11px;color:var(--color-text-muted);transition:transform .2s;display:inline-block;transform:rotate(${open ? 180 : 0}deg)`}>▼</span>
      </button>
      {open && (
        <div style="padding:0 14px 8px">
          {/* Supervisor row */}
          {supervisors.length > 0 && (
            <div style="margin-bottom:6px">
              {supervisors.map(m => (
                <div
                  key={m.id}
                  onClick={() => onSelect && onSelect(m)}
                  style={`display:flex;align-items:center;gap:10px;padding:6px 0 5px;border-bottom:1px dashed var(--color-border);${onSelect ? 'cursor:pointer' : ''}`}
                >
                  <Avatar name={m.display_name} size={30} />
                  <div style="min-width:0;flex:1">
                    <div style="font-size:12px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{m.display_name} <AwayMark userId={m.id} /></div>
                    <div style="font-size:11px;color:var(--color-primary);font-weight:600">{m.roles.join(' · ')}</div>
                  </div>
                  <span style="font-size:10px;font-weight:700;color:var(--color-primary);background:var(--color-primary-light,#ede9fe);padding:2px 6px;border-radius:6px;white-space:nowrap;flex-shrink:0">verantwoordelijke</span>
                </div>
              ))}
            </div>
          )}

          {/* Members: grouped by role when multiple roles exist */}
          {commission.members.length === 0
            ? <div style="padding:4px 0 6px;font-size:12px;color:var(--color-text-muted);font-style:italic">Nog geen leden toegewezen</div>
            : grouped
              ? roleGroups.map(role => (
                  <div key={role} style="margin-bottom:6px">
                    <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);padding:6px 0 3px;border-top:1px solid var(--color-border)">
                      {role}
                    </div>
                    {seen.get(role).map(m => (
                      <div
                        key={m.id}
                        onClick={() => onSelect && onSelect(m)}
                        style={`display:flex;align-items:center;gap:8px;padding:4px 0;${onSelect ? 'cursor:pointer' : ''}`}
                      >
                        <Avatar name={m.display_name} size={26} />
                        <span style="font-size:12px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1">{m.display_name}</span>
                        <AwayMark userId={m.id} />
                        {onSelect && <span style="font-size:12px;color:var(--color-text-muted)">›</span>}
                      </div>
                    ))}
                  </div>
                ))
              : commission.members.map(m => <MemberRow key={m.id} member={m} onSelect={onSelect} />)
          }
        </div>
      )}
    </div>
  );
}

function SectionBlock({ section, defaultOpen, onSelect }) {
  const [open, setOpen] = useState(defaultOpen);
  const totalMembers = section.directMembers.length +
    section.commissions.reduce((sum, c) => sum + c.members.length, 0);

  // Deduplicate: some members appear in multiple commissions
  const allIds = new Set([
    ...section.directMembers.map(m => m.id),
    ...section.commissions.flatMap(c => c.members.map(m => m.id)),
  ]);

  const isBoard = section.directMembers.length > 0 && section.commissions.length === 0;

  return (
    <div style={`border-radius:14px;overflow:hidden;border:2px solid ${isBoard ? 'var(--color-primary)' : 'var(--color-border)'};background:var(--color-surface)`}>
      {/* Section header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={`width:100%;display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border:none;cursor:pointer;text-align:left;gap:8px;background:${isBoard ? 'var(--color-primary)' : 'var(--color-surface-alt,#f9fafb)'}`}
      >
        <div style="display:flex;align-items:center;gap:10px">
          <span style={`font-size:15px;font-weight:700;${isBoard ? 'color:#fff' : 'color:var(--color-text)'}`}>
            {section.name}
          </span>
          <span style={`font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;${isBoard ? 'background:rgba(255,255,255,.25);color:#fff' : 'background:var(--color-border);color:var(--color-text-muted)'}`}>
            {allIds.size} {allIds.size === 1 ? 'persoon' : 'personen'}
          </span>
        </div>
        <span style={`font-size:11px;${isBoard ? 'color:rgba(255,255,255,.8)' : 'color:var(--color-text-muted)'};transition:transform .2s;display:inline-block;transform:rotate(${open ? 180 : 0}deg)`}>▼</span>
      </button>

      {open && (
        <div style="padding:16px 18px;display:flex;flex-direction:column;gap:10px">
          {/* Direct members (e.g. board roles) */}
          {section.directMembers.length > 0 && (
            <div style={`border-radius:10px;overflow:hidden;border:1px solid ${isBoard ? 'var(--color-primary)20' : 'var(--color-border)'};background:var(--color-surface)`}>
              {section.directMembers.map(m => (
                <div key={m.id} style="padding:0 14px;border-bottom:1px solid var(--color-border)">
                  <MemberRow member={m} onSelect={onSelect} />
                </div>
              ))}
            </div>
          )}

          {/* Commissions */}
          {section.commissions.length > 0 && (
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:10px">
              {section.commissions.map(c => <CommissionCard key={c.id} commission={c} onSelect={onSelect} />)}
            </div>
          )}

          {section.directMembers.length === 0 && section.commissions.length === 0 && (
            <p style="font-size:13px;color:var(--color-text-muted);font-style:italic">Geen leden of commissies gevonden.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrgChartView() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const away = useAway('org');

  useEffect(() => {
    api('/api/settings/org-chart')
      .then(setSections)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? sections.map(s => ({
        ...s,
        directMembers: s.directMembers.filter(m =>
          m.display_name?.toLowerCase().includes(search.toLowerCase()) ||
          m.roles.some(r => r.toLowerCase().includes(search.toLowerCase()))
        ),
        commissions: s.commissions.map(c => ({
          ...c,
          members: c.members.filter(m =>
            m.display_name?.toLowerCase().includes(search.toLowerCase()) ||
            m.roles.some(r => r.toLowerCase().includes(search.toLowerCase())) ||
            c.name.toLowerCase().includes(search.toLowerCase())
          ),
        })).filter(c => c.members.length > 0 || c.name.toLowerCase().includes(search.toLowerCase())),
      })).filter(s =>
        s.directMembers.length > 0 || s.commissions.length > 0 ||
        s.name.toLowerCase().includes(search.toLowerCase())
      )
    : sections;

  return (
    <AwayContext.Provider value={away}>
    <div>
      <MemberProfileModal member={selectedMember} onClose={() => setSelectedMember(null)} />

      <div class="page-header" style="margin-bottom:16px">
        <h1 class="page-title">Organisatie</h1>
      </div>

      <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 16px">
        Overzicht van secties, commissies en wie waarvoor verantwoordelijk is.
      </p>

      <input
        class="form-input"
        type="search"
        placeholder="Zoek persoon of functie…"
        value={search}
        onInput={e => setSearch(e.target.value)}
        style="max-width:320px;margin-bottom:20px"
      />

      {loading && (
        <div style="display:flex;flex-direction:column;gap:12px">
          {[0,1,2,3].map(i => (
            <div key={i} class="skeleton" style="height:56px;border-radius:14px" />
          ))}
        </div>
      )}

      {!loading && (
        <div style="display:flex;flex-direction:column;gap:14px">
          {filtered.map((section, i) => (
            <SectionBlock key={section.id} section={section} defaultOpen={i === 0} onSelect={m => setSelectedMember(m)} />
          ))}
          {filtered.length === 0 && (
            <div class="text-center text-muted" style="padding:60px">Geen resultaten gevonden.</div>
          )}
        </div>
      )}
    </div>
    </AwayContext.Provider>
  );
}
